import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ShopOffersInventory } from '@app/shared/models/inventory.model';
import { LimitedTimeEventService } from '@app/shared/services/limitedTimeEvent.service';
import { LoggerService } from '@app/shared/services/logger.service';
import { ModalManagerService } from '@app/shared/services/modal-manager.service';
import { ShopService } from '@app/shared/services/shop.service';
import { RealtimeService } from '@app/shared/services/realtime.service';
import { TagsService } from '@app/shared/services/tags.service';
import { AppService } from '@app/app.service';
import { lastValueFrom } from 'rxjs';
import { ShopRatesRepository } from '@app/shared/state/shop-rates/shop-rates.repository';
import { TagsRepository } from '@app/shared/state/tags/tags.repository';
import { UserRepository } from '@app/shared/state/user/user.repository';
import { ProgressLineFacade } from '@app/progress-line/progress-line.facade';
import { ProgressLineService } from '@app/progress-line/progress-line.service';
import { UserService } from '@app/shared/services/user.service';
import { TokenService } from '@app/shared/services/token.service';

@Injectable({
  providedIn: 'root',
})
export class HomeService {
  private loaded: boolean;

  private readonly logger = inject(LoggerService);
  private readonly modalManagerService = inject(ModalManagerService); //NO BORRAR
  private readonly userRepo = inject(UserRepository);
  private readonly shopService = inject(ShopService);
  private readonly shopRatesRepo = inject(ShopRatesRepository);
  private readonly appService = inject(AppService);
  private readonly realtimeService = inject(RealtimeService);
  private readonly tagsService = inject(TagsService);
  private readonly tagsRepo = inject(TagsRepository);
  private readonly limitedTimeEventService = inject(LimitedTimeEventService);
  private readonly progressLineFacade = inject(ProgressLineFacade);
  private readonly progressLineService = inject(ProgressLineService);
  private readonly userService = inject(UserService);
  private readonly tokenService = inject(TokenService);

  async init() {
    if (this.loaded) return;

    try {
      await this.migrateUserToProgressLine();

      if (!this.userRepo.getAccountDeletionEnabled()) {
        await this.realtimeService.start();
      }

      await Promise.all([this.fetchCasino(), this.fetchLimitedTimeEvent(), this.fetchTags()]);

      this.subscribeRealtimeEvents();
      this.loaded = true;
      this.fetchShopRates();
    } catch (err) {
      this.loaded = false;
      throw err;
    }
  }

  private subscribeRealtimeEvents() {
    this.realtimeService.on('MultipleTabs').subscribe(async () => {
      await this.realtimeService.stop();
      await this.appService.goToMultiTab();
    });
  }

  private fetchLimitedTimeEvent() {
    this.limitedTimeEventService.requestLimitedTimeEvents();
  }

  private async fetchShopRates(): Promise<ShopOffersInventory> {
    try {
      const inventory = await lastValueFrom(this.shopService.getShopWithPrices());
      this.shopRatesRepo.setShopRates(inventory);
      return inventory;
    } catch (error) {
      const emptyShopRates: ShopOffersInventory = { Coins: [], Gems: [] };
      this.logger.error('Error fetching shop rates:', error);
      if (error instanceof HttpErrorResponse) {
        switch (error.status) {
          case 422:
            this.logger.log('(SLOTCOM) - 422 - Shop Inventory param error.');
            break;
          case 423:
            this.logger.log('(SLOTCOM) - 423 - Shop Blocked by Flag.');
            break;
          case 500:
            this.logger.log('(SLOTCOM) - 500 - Shop Inventory server error.');
            break;
          default:
            this.logger.log(`(SLOTCOM) - Shop Inventory HTTP Error: ${error.status}`);
            break;
        }
      }
      this.shopRatesRepo.setShopRates(emptyShopRates);
      return emptyShopRates;
    }
  }

  private fetchTags() {
    return this.tagsService.getTags().subscribe({
      next: (tags) => {
        this.tagsRepo.updateTags(this.tagsService.orderByPriority(tags));
      },
      error: (error) => this.logger.error(error),
    });
  }

  private async fetchCasino() {
    await this.progressLineFacade.refreshUserCasino();
  }

  private async migrateUserToProgressLine(): Promise<void> {
    if (this.userRepo.getUser().useProgressLine) return;

    await this.progressLineService.migrate();
    await this.tokenService.refreshToken();
    await this.userService.fetchCurrentUser();
  }
}
